import React, { useEffect, useState } from 'react';
import { leaveEncashmentAPI } from '../../../../services';
import { LoadingSpinner } from '../../../UI';

const currency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Live formula preview — calls the same calculation engine the real request path
// uses, so what HR sees while building the formula matches what employees get.
// Debounced so it doesn't fire an API call on every keystroke.
const LedgerPreview = ({ employeeId, leaveType, triggerType, daysRequested }) => {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!employeeId || !leaveType || !triggerType || !daysRequested || Number(daysRequested) <= 0) {
      setLedger(null);
      setError('');
      return undefined;
    }

    const handle = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const res = await leaveEncashmentAPI.previewRequest({ employeeId, leaveType, triggerType, daysRequested: Number(daysRequested) });
        setLedger(res.data?.data || res.data);
      } catch (err) {
        setError(err.message || 'Could not calculate preview');
        setLedger(null);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [employeeId, leaveType, triggerType, daysRequested]);

  if (loading) {
    return <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>;
  }

  if (error) {
    return <p className="text-sm text-danger-600">{error}</p>;
  }

  if (!ledger) {
    return <p className="text-sm text-secondary-500">Enter an employee, leave type and days to preview the calculation.</p>;
  }

  const rows = [
    ['Basic', ledger.components?.basic],
    ['DA', ledger.components?.da],
    ['HRA', ledger.components?.hra],
    ['Special Allowance', ledger.components?.special],
  ].filter(([, v]) => v > 0);

  return (
    <div className="bg-secondary-50 rounded-lg p-4 space-y-2 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between text-secondary-600">
          <span>{label}</span>
          <span>{currency(value)}</span>
        </div>
      ))}
      <div className="flex justify-between font-medium text-secondary-900 border-t border-secondary-200 pt-2">
        <span>Component Sum</span>
        <span>{currency(ledger.componentSum)}</span>
      </div>
      <div className="flex justify-between text-secondary-600">
        <span>÷ Divisor</span>
        <span>{ledger.divisor}</span>
      </div>
      <div className="flex justify-between text-secondary-600">
        <span>Per-Day Rate</span>
        <span>{currency(ledger.perDayRate)}</span>
      </div>
      <div className="flex justify-between text-secondary-600">
        <span>× Days Requested</span>
        <span>{ledger.daysRequested}</span>
      </div>
      <div className="flex justify-between text-base font-bold text-primary-700 border-t border-secondary-200 pt-2">
        <span>Final Amount</span>
        <span>{currency(ledger.finalAmount)}</span>
      </div>
    </div>
  );
};

export default LedgerPreview;
